import { propAreaSqm, sqmToRadp, sqmToAana, AANA_TO_SQM, areaDisplay } from "../utils/areaConversions";
import { toWords, toWordsNepali, toDevanagariNum } from "../utils/numberWords";
import { esc } from "../utils/htmlEscape";

export function buildPreliminaryHTML(s, suggestedFilename, autoPrint = false, mapSnapshots = {}) {
  const vi = s.valuatorInfo||{};
  const lhSrc = s.letterheadPng || "";
  const lhBlock = lhSrc
    ? `<div style="width:100%;overflow:hidden;margin-bottom:0;-webkit-print-color-adjust:exact;print-color-adjust:exact"><img src="${lhSrc}" style="width:100%;display:block;height:auto;object-fit:contain;object-position:top center" alt="Company Letterhead"/></div>`
    : "";
  const clientLine = (s.clients||[]).map(cl=>{
    const parts=[];
    if(cl.showPerson&&cl.person?.name) parts.push(cl.person.name);
    if(cl.showCompany&&cl.company?.name) parts.push(cl.company.name);
    return parts.join(" / ");
  }).filter(Boolean).join(", ")||"—";

  const mortProps = (s.properties||[]).filter(p=>(s.mortgagedIds||[]).includes(p.id));

  const personRows = (person) => `
    <tr><td>Name</td><td>${esc(person.name)}</td><td>Citizenship No.</td><td>${esc(person.citizenshipNo)}</td></tr>
    <tr><td>Issued Date (BS)</td><td>${esc(person.issuedDate)}</td><td>Issued By</td><td>${esc(person.issuedBy)}</td></tr>
    <tr><td>Father's Name</td><td>${esc(person.fatherName)}</td><td>Grandfather's Name</td><td>${esc(person.grandfatherName)}</td></tr>
    <tr><td>Husband's Name</td><td>${esc(person.husbandName)}</td><td>Contact</td><td>${esc(person.contact)}</td></tr>
    <tr><td>Address</td><td colspan="3">${esc(person.address)}</td></tr>`;

  // 3.1 Clients
  const clientsHTML = (s.clients||[]).map((cl,i)=>{
    let html = `<p class="sub-head">Client ${i+1}</p>`;
    if(cl.showPerson) html += `<table class="info-table">${personRows(cl.person||{})}</table>`;
    if(cl.showCompany){
      html += `<table class="info-table" style="margin-top:6px">
        <tr><td>Company Name</td><td>${esc(cl.company?.name)}</td><td>PAN/VAT</td><td>${esc(cl.company?.panVat)}</td></tr>
        <tr><td>Reg. No.</td><td>${esc(cl.company?.regNo)}</td><td>Reg. Date (BS)</td><td>${esc(cl.company?.regDate)}</td></tr>
        <tr><td>Registered On</td><td colspan="3">${esc(cl.company?.regOn)}</td></tr>
        <tr><td>Contact</td><td>${esc(cl.company?.contact)}</td><td>Address</td><td>${esc(cl.company?.address)}</td></tr>
      </table>`;
      (cl.company?.directors||[]).forEach((d,di)=>{
        html += `<p style="margin:6px 0 3px;font-style:italic;font-size:11pt">Director ${di+1}${d.name?": "+esc(d.name):""}</p><table class="info-table">${personRows(d)}</table>`;
      });
    }
    return html;
  }).join("");

  // 3.2 Owners
  const ownersInfoHTML = (s.owners||[]).map((ow,i)=>{
    let html = `<p class="sub-head">Owner ${i+1}</p>`;
    if(ow.showPerson) html += `<table class="info-table">${personRows(ow.person||{})}</table>`;
    if(ow.showCompany){
      html += `<table class="info-table" style="margin-top:6px">
        <tr><td>Company Name</td><td>${esc(ow.company?.name)}</td><td>PAN/VAT</td><td>${esc(ow.company?.panVat)}</td></tr>
        <tr><td>Reg. No.</td><td>${esc(ow.company?.regNo)}</td><td>Reg. Date (BS)</td><td>${esc(ow.company?.regDate)}</td></tr>
        <tr><td>Registered On</td><td colspan="3">${esc(ow.company?.regOn)}</td></tr>
        <tr><td>Contact</td><td>${esc(ow.company?.contact)}</td><td>Address</td><td>${esc(ow.company?.address)}</td></tr>
      </table>`;
      (ow.company?.directors||[]).forEach((d,di)=>{
        html += `<p style="margin:6px 0 3px;font-style:italic;font-size:11pt">Director ${di+1}${d.name?": "+esc(d.name):""}</p><table class="info-table">${personRows(d)}</table>`;
      });
    }
    return html;
  }).join("");

  // 3.3 Property Details
  const propDetailRows = (s.properties||[]).map(p=>{
    const sqm = propAreaSqm(p);
    const radp = sqmToRadp(sqm);
    return `<tr>
      <td>${esc(p.plotNo)}</td><td>${esc(p.traceSheetNo)}</td><td>${esc(p.landType)}</td><td>${esc(p.category)}</td>
      <td>${p.areaUnit==="radp"?`${p.areaRadp?.r||0}-${p.areaRadp?.a||0}-${p.areaRadp?.p||0}-${p.areaRadp?.d||0}`:`${p.areaSqm||0} sq.m`}</td>
      <td>${sqm.toFixed(2)}</td><td>${radp.r}-${radp.a}-${radp.p}-${radp.d}</td>
      <td>${esc(p.ownerName)}</td><td>${esc(p.addressLalpurja)}</td>
      <td>${esc(p.location)}</td><td>${esc(p.googlePlusCode)}</td>
    </tr>`;
  }).join("");

  // 4. Area to be Mortgaged
  const mortAreaRows = mortProps.map(p=>{
    const lSqm = propAreaSqm(p);
    const measured = parseFloat(s.areaMeasured?.[p.id])||lSqm;
    const radp = sqmToRadp(lSqm);
    return `<tr><td>${esc(p.plotNo)}</td><td>${esc(p.traceSheetNo)}</td><td>${esc(p.ownerName)}</td><td>${areaDisplay(p)}</td><td>${radp.r}-${radp.a}-${radp.p}-${radp.d}</td><td>${lSqm.toFixed(2)}</td><td>${measured.toFixed(2)}</td></tr>`;
  }).join("");

  // 5. Deduction Declaration
  const deductionRows = mortProps.map(p=>`
    <tr><td>${esc(p.plotNo)}</td><td>${esc(s.deductions?.[p.id]?.dim)}</td><td>${s.deductions?.[p.id]?.area||"0"}</td><td>${esc(s.deductions?.[p.id]?.reason)}</td></tr>`).join("");

  // 6. Considered Area
  const consideredRows = mortProps.map(p=>{
    const lSqm = propAreaSqm(p);
    const measured = parseFloat(s.areaMeasured?.[p.id])||lSqm;
    const deduct = parseFloat(s.deductions?.[p.id]?.area)||0;
    const considered = Math.max(0, Math.min(lSqm, measured) - deduct);
    const radp = sqmToRadp(considered);
    return `<tr><td>${esc(p.plotNo)}</td><td>${lSqm.toFixed(2)}</td><td>${measured.toFixed(2)}</td><td>${deduct.toFixed(2)}</td><td><strong>${considered.toFixed(2)}</strong></td><td><strong>${radp.r}-${radp.a}-${radp.p}-${radp.d}</strong></td><td><strong>${sqmToAana(considered).toFixed(4)}</strong></td></tr>`;
  }).join("");

  // 7. Access to Property (Road Access)
  const roadAccessHTML = (s.properties||[]).map(p=>{
    const rows = (s.roadAccess?.[p.id]||[]);
    if(!rows.length) return `<tr><td>${esc(p.plotNo)}</td><td colspan="5" style="font-style:italic;color:#888">No access rows entered</td></tr>`;
    return rows.map(r=>`<tr><td>${esc(p.plotNo)}</td><td>${esc(r.roadType)}</td><td>${esc(r.frontage)}</td><td>${esc(r.widthField)}</td><td>${esc(r.widthTrace)}</td><td>${esc(r.remarks)}</td></tr>`).join("");
  }).join("");

  // 8. Boundary Declaration
  const boundaryRows = mortProps.map(p=>{
    const ac = s.access?.[p.id]||{};
    return `<tr>
      <td>${esc(p.plotNo)}</td>
      <td>${esc(ac.east)}</td><td>${esc(ac.west)}</td><td>${esc(ac.north)}</td><td>${esc(ac.south)}</td>
      <td>${esc(ac.remarks)}</td>
    </tr>`;
  }).join("");

  // 9. Building Detail
  const buildingDetailHTML = (s.buildings||[]).map((b,i)=>{
    const totalActual=(b.areaTable||[]).reduce((sum,a)=>sum+(parseFloat(a.areaActual)||0),0);
    const totalApproved=(b.areaTable||[]).reduce((sum,a)=>sum+(parseFloat(a.areaApproved)||0),0);
    const totalCert=(b.areaTable||[]).reduce((sum,a)=>sum+(parseFloat(a.areaCertificate)||0),0);
    const areaRows=(b.areaTable||[]).map(a=>`
      <tr><td>${esc(a.description)}</td><td>${parseFloat(a.areaActual)||0}</td><td>${parseFloat(a.areaApproved)||0}</td><td>${parseFloat(a.areaCertificate)||0}</td></tr>`).join("");
    return ("" + `    <p class="sub-head">Building ${i+1}${b.plotNo?` — Plot No. ${esc(b.plotNo)}`:""}</p>
    <table class="info-table">
      <tr><td>Owner Name</td><td>${esc(b.ownerName)}</td><td>Plot No.</td><td>${esc(b.plotNo)}</td></tr>
      <tr><td>Face Direction</td><td>${esc(b.faceDirection)}</td><td>No. of Floors</td><td>${esc(b.numFloors)}</td></tr>
      <tr><td>Permitted Floors</td><td>${esc(b.floorPermission)}</td><td>Year of Construction</td><td>${esc(b.yearOfConstruction)}</td></tr>
      <tr><td>Completion Date (BS)</td><td>${esc(b.completionDate)}</td><td>Age of Building (yrs)</td><td>${esc(b.ageOfBuilding)}</td></tr>
      <tr><td>Expected Life (yrs)</td><td>${esc(b.expectedLife)}</td><td>Structure Type</td><td>${esc(b.structureType)}</td></tr>
      <tr><td>Foundation Type</td><td colspan="3">${esc(b.foundationType)}</td></tr>
    </table>
    <table>
      <thead><tr><th>Floor / Description</th><th>Actual Construction (sq.ft)</th><th>Approved Map (sq.ft)</th><th>Completion Cert. (sq.ft)</th></tr></thead>
      <tbody>${areaRows||`<tr><td colspan="4" style="text-align:center;font-style:italic">No area data</td></tr>`}
        <tr class="total-row"><td><strong>TOTAL</strong></td><td><strong>${totalActual.toFixed(2)}</strong></td><td><strong>${totalApproved.toFixed(2)}</strong></td><td><strong>${totalCert.toFixed(2)}</strong></td></tr>
      </tbody>
    </table>`);
  }).join('<div style="border-top:1px dashed #aaa;margin:10px 0"></div>');

  // 10. Valuation of Land
  let totalLandCommercial = 0, totalLandFMV = 0;
  const landValRows = mortProps.map(p=>{
    const lSqm = propAreaSqm(p);
    const measured = parseFloat(s.areaMeasured?.[p.id])||lSqm;
    const deduct = parseFloat(s.deductions?.[p.id]?.area)||0;
    const considered = Math.max(0, Math.min(lSqm, measured) - deduct);
    const radp = sqmToRadp(considered);

    const splits = (s.plotRateSplits?.[p.id]||[]);
    const hasSplits = splits.length > 0;

    if (hasSplits) {
      // Multi-zone rows
      let plotCommTotal = 0, plotFmvTotal = 0;
      const zoneRows = splits.map(sp=>{
        const spArea = parseFloat(sp.areaSqm)||0;
        const spCRate = parseFloat(sp.commercialRate)||0;
        const spCW = sp.commercialWeight !== undefined ? parseFloat(sp.commercialWeight) : 70;
        const spGW = sp.govWeight !== undefined ? parseFloat(sp.govWeight) : 30;
        const spFmvRate = (spCRate * spCW / 100) + ((parseFloat(sp.govRate)||0) * spGW / 100);
        const spCVal = spArea * spCRate / AANA_TO_SQM;
        const spFVal = spArea * spFmvRate / AANA_TO_SQM;
        plotCommTotal += spCVal;
        plotFmvTotal  += spFVal;
        return `<tr>
          <td style="padding-left:18px;font-style:italic;color:#555">${esc(p.plotNo)} (${esc(sp.label, "Zone")})</td>
          <td>${spArea.toFixed(2)}</td>
          <td>${sqmToRadp(spArea).r}-${sqmToRadp(spArea).a}-${sqmToRadp(spArea).p}-${sqmToRadp(spArea).d}</td>
          <td>${sqmToAana(spArea).toFixed(4)}</td>
          <td>${spCRate.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
          <td>${spFmvRate.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
          <td>${spCVal.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
          <td>${spFVal.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        </tr>`;
      }).join("");
      const cValR = Math.floor(plotCommTotal / 100) * 100;
      const fValR = Math.floor(plotFmvTotal / 100) * 100;
      totalLandCommercial += cValR;
      totalLandFMV += fValR;
      return `${zoneRows}
      <tr style="background:#f0f0f0;font-weight:bold">
        <td>${esc(p.plotNo)} — Sub-total</td>
        <td>${considered.toFixed(2)}</td>
        <td>${radp.r}-${radp.a}-${radp.p}-${radp.d}</td>
        <td>${sqmToAana(considered).toFixed(4)}</td>
        <td colspan="2" style="text-align:center;font-style:italic;color:#888">Multiple rates</td>
        <td><strong>${plotCommTotal.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></td>
        <td><strong>${plotFmvTotal.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></td>
      </tr>
      <tr style="background:#e8f5e9">
        <td colspan="6" style="font-size:10pt;color:#555;padding-left:14px"><em>Rounded Down (nearest 100)</em></td>
        <td><strong style="color:#2e7d32">NPR ${cValR.toLocaleString("en-NP")}</strong></td>
        <td><strong style="color:#2e7d32">NPR ${fValR.toLocaleString("en-NP")}</strong></td>
      </tr>`;
    } else {
      // Single-rate row
      const r = s.rates?.[p.id]||{};
      const cRate = parseFloat(r.commercialRate)||0;
      const govRate = parseFloat(r.govRate)||0;
      const commW = parseFloat(r.commercialWeight)||70;
      const govW  = parseFloat(r.govWeight)||30;
      const fmvRate = (cRate * commW / 100) + (govRate * govW / 100);
      const cVal = considered * cRate / AANA_TO_SQM;
      const fVal = considered * fmvRate / AANA_TO_SQM;
      const cValR = Math.floor(cVal / 100) * 100;
      const fValR = Math.floor(fVal / 100) * 100;
      totalLandCommercial += cValR;
      totalLandFMV += fValR;
      return `<tr>
        <td>${esc(p.plotNo)}</td>
        <td>${considered.toFixed(2)}</td>
        <td>${radp.r}-${radp.a}-${radp.p}-${radp.d}</td>
        <td>${sqmToAana(considered).toFixed(4)}</td>
        <td>${cRate.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td>${fmvRate.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td>${cVal.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
        <td>${fVal.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
      </tr>
      <tr style="background:#e8f5e9">
        <td colspan="6" style="font-size:10pt;color:#555;padding-left:14px"><em>Rounded Down (nearest 100)</em></td>
        <td><strong style="color:#2e7d32">NPR ${cValR.toLocaleString("en-NP")}</strong></td>
        <td><strong style="color:#2e7d32">NPR ${fValR.toLocaleString("en-NP")}</strong></td>
      </tr>`;
    }
  }).join("");

  // 11. Valuation of Building
  const bv = s.buildingVals || {};
  let totalBuildingValue = 0;
  const buildingValHTML = (s.buildings||[]).map((b,i)=>{
    const v = bv[b.id] || {};
    const floorRates = v.floorRates||{};
    const floorCalcs = (b.areaTable||[]).map(row=>{
      const area = parseFloat(row.areaActual)||0;
      const rate = parseFloat(floorRates[row.id])||0;
      return { description: row.description||"—", area, rate, cost: area*rate };
    });
    const baseCost = floorCalcs.reduce((s,f)=>s+f.cost,0);
    const totalArea = floorCalcs.reduce((s,f)=>s+f.area,0);
    const sanCost = baseCost * (parseFloat(v.sanitaryPct)||0) / 100;
    const elecCost = baseCost * (parseFloat(v.electricalPct)||0) / 100;
    const finCost = baseCost * (parseFloat(v.finishingPct)||0) / 100;
    const totalWithFix = baseCost + sanCost + elecCost + finCost;
    const age = parseFloat(b.ageOfBuilding)||0;
    const depRate = parseFloat(v.depreciationRate)||2.25;
    const totalDepPct = Math.min(100, age * depRate);
    const totalDep = totalWithFix * totalDepPct / 100;
    const actual = Math.max(0, totalWithFix - totalDep);
    const rounded = Math.floor(actual / 100) * 100;
    totalBuildingValue += rounded;
    const floorRows = floorCalcs.map(f=>`<tr><td>${esc(f.description)}</td><td>${f.area.toFixed(2)}</td><td>${f.rate.toLocaleString("en-NP")}</td><td>${f.cost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>`).join("");
    return ("" + `    <p class="sub-head">Building ${i+1}${b.plotNo?` — Plot ${esc(b.plotNo)}`:""}</p>
    <table>
      <thead><tr><th>Floor</th><th>Area (sq.ft)</th><th>Rate (NPR/sq.ft)</th><th>Floor Cost (NPR)</th></tr></thead>
      <tbody>${floorRows}
        <tr class="total-row"><td colspan="3"><strong>Total Base Construction Cost</strong></td><td><strong>${baseCost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></td></tr>
      </tbody>
    </table>
    <table style="margin-top:4px">
      <thead><tr><th>Description</th><th>Calculation</th><th>Amount (NPR)</th></tr></thead>
      <tbody>
        <tr><td>Total Base Construction Cost</td><td>${totalArea.toFixed(2)} sq.ft</td><td>${baseCost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
        <tr><td>Sanitary Cost</td><td>${parseFloat(v.sanitaryPct)||0}% of base</td><td>${sanCost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
        <tr><td>Electrical Cost</td><td>${parseFloat(v.electricalPct)||0}% of base</td><td>${elecCost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
        <tr><td>Finishing Cost</td><td>${parseFloat(v.finishingPct)||0}% of base</td><td>${finCost.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
        <tr style="background:#f0e8d8"><td><strong>Total Cost (with fixtures)</strong></td><td></td><td><strong>${totalWithFix.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></td></tr>
        <tr><td>Total Depreciation</td><td>${age} yr × ${depRate}% = ${totalDepPct.toFixed(2)}%</td><td>− ${totalDep.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</td></tr>
        <tr class="total-row"><td colspan="2"><strong>Actual Cost of Building (Depreciated)</strong></td><td><strong>NPR ${actual.toLocaleString("en-NP",{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></td></tr>
        <tr style="background:#e8f5e9"><td><strong>Rounded Down Cost (nearest 100)</strong></td><td style="color:#666;font-size:10pt">floor(÷100)×100</td><td><strong style="color:#2e7d32">NPR ${rounded.toLocaleString("en-NP")}</strong></td></tr>
      </tbody>
    </table>`);
  }).join('<div style="border-top:1px dashed #aaa;margin:8px 0"></div>');

  // 12. Summary
  const fmv = Math.floor((totalLandFMV + totalBuildingValue) / 100) * 100;
  const commercialVal = Math.floor((totalLandCommercial + totalBuildingValue) / 100) * 100;
  const distressMult = Math.min(100, Math.max(0, parseFloat(s.distressPct)||80)) / 100;
  const distressVal = Math.floor(fmv * distressMult / 100) * 100;
  // Government value
  const govVals = s.govValues || {};
  const totalGovLand = (s.properties||[]).filter(p=>(s.mortgagedIds||[]).includes(p.id)).reduce((sum,p)=>{
    const r = (s.rates||{})[p.id]||{};
    const splits = ((s.plotRateSplits||{})[p.id]||[]);
    const gRate = splits.length > 0
      ? (parseFloat(splits[0].govRate)||0)
      : (parseFloat(r.govRate)||0);
    const am = parseFloat((s.areaMeasured||{})[p.id])||0;
    const lSqm = (()=>{if(p.areaUnit==='sqm')return parseFloat(p.areaSqm)||0;const r=p.areaRadp||{};return(parseFloat(r.r)||0)*508.72+(parseFloat(r.a)||0)*31.795+(parseFloat(r.p)||0)*7.949+(parseFloat(r.d)||0)*1.987;})();
    const ded = parseFloat(((s.deductions||{})[p.id]||{}).area)||0;
    const ca = Math.max(0,(am||lSqm)-ded);
    return sum + gRate*(ca/31.795);
  },0);
  const finalGovVal = Math.floor(totalGovLand/100)*100;

  const autoScript = autoPrint
    ? `<script>window.addEventListener('load',function(){setTimeout(function(){window.focus();window.print();},600);});<\/script>`
    : "";

  // Pre-build photo pages outside the template literal to avoid nested interpolation issues
  const prelim_photoPages = (() => {
    const photos = (s.photos||[]).filter(p => p && (p.dataUrl||p.data));
    if (!photos.length) return "";
    const PER_PAGE = 4;
    const pages = [];
    for (let i = 0; i < photos.length; i += PER_PAGE) pages.push(photos.slice(i, i + PER_PAGE));
    const separator = "<div class=\"photo-sep\">" +
      "<div style=\"text-align:center;font-family:'Times New Roman',Times,serif;\">" +
        "<div style=\"width:80mm;height:3pt;background:#2c1810;margin:0 auto 12pt;\"></div>" +
        "<div style=\"font-size:22pt;font-weight:bold;text-transform:uppercase;letter-spacing:4px;color:#2c1810;\">Property Photographs</div>" +
        "<div style=\"font-size:11pt;color:#666;margin-top:6pt;letter-spacing:1px;\">" + photos.length + " photograph" + (photos.length !== 1 ? "s" : "") + "</div>" +
        "<div style=\"width:80mm;height:3pt;background:#2c1810;margin:12pt auto 0;\"></div>" +
      "</div></div>";
    return separator + pages.map((group, pi) => {
      const cells = group.map((ph, ci) => {
        const num = pi * PER_PAGE + ci + 1;
        const capRaw = ph.caption || ph.label || ("Photo " + num);
        const capEsc = esc(capRaw);
        const src = ph.dataUrl || ph.data || "";
        return "<div class=\"photo-cell\"><img src=\"" + src + "\" class=\"photo-img\" alt=\"" + capEsc + "\"/><div class=\"photo-caption\">" + num + ". " + capEsc + "</div></div>";
      }).join("");
      const pad = Array(PER_PAGE - group.length).fill("<div class=\"photo-cell photo-cell-empty\"></div>").join("");
      const pageNum = pi + 1;
      const pageTotal = pages.length;
      return "<div class=\"photo-page\">" +
        "<div class=\"photo-page-header\"><span>Property Photographs</span><span>Page " + pageNum + " of " + pageTotal + "</span></div>" +
        "<div class=\"photo-grid-4\">" + cells + pad + "</div>" +
        "</div>";
    }).join("");
  })();
  const purifyScript = `<script>
  window.addEventListener('load', function() {
    if (typeof DOMPurify === 'undefined') return;
    var els = document.querySelectorAll('td, p, li, span, div');
    els.forEach(function(el) {
      if (el.children.length === 0 && el.innerHTML !== el.textContent) {
        el.innerHTML = DOMPurify.sanitize(el.innerHTML, { ALLOWED_TAGS: ['strong','em','br','b','i'] });
      }
    });
  });
  <\/script>`;

  return `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'self' 'unsafe-inline' data: blob:; script-src 'self' 'unsafe-inline' https://cdnjs.cloudflare.com;">
  <title>${suggestedFilename||"Preliminary Valuation Report"}</title>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/dompurify/3.0.6/purify.min.js"><\/script>
  <style>
    @page {
      size: A4 portrait;
      margin: 20mm 20mm 14mm 35mm;
    }
    @media print {
      .page-footer {
        position: fixed; bottom: 0; left: 0; right: 0;
        height: 12mm;
        display: flex; align-items: center; justify-content: space-between;
        padding: 0 20mm 0 35mm;
        font-family: "Times New Roman", Times, serif;
        font-size: 8.5pt; color: #555;
        border-top: 0.5pt solid #ccc;
        background: white;
      }
    }
    @media screen { .page-footer { display: none; } }
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: "Times New Roman", Times, serif;
      font-size: 12pt;
      line-height: 1.5;
      color: #000;
      background: #fff;
    }
    .page {
      max-width: 160mm;
      margin: 0 auto;
      padding: 0;
    }
    .no-print {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 20px;
      background: #1a1714; color: #fff;
      font-family: Arial, sans-serif; font-size: 13px;
      position: sticky; top: 0; z-index: 9999;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    }
    .no-print button {
      padding: 7px 20px; border: none; border-radius: 5px;
      font-weight: 700; cursor: pointer; font-size: 13px;
    }
    .no-print .btn-print { background: #f9c74f; color: #1a1714; }
    .no-print .btn-close  { background: rgba(255,255,255,0.15); color: #fff; border: 1px solid rgba(255,255,255,0.3); }
    @media print {
      .no-print { display:none !important; height:0 !important; overflow:hidden !important; }
      html, body { background:white !important; margin:0 !important; padding:0 !important; }
      .page { max-width:100%; margin:0; }
      body { print-color-adjust:exact; -webkit-print-color-adjust:exact; }
    }
    @page photo-p { size:A4 landscape; margin:10mm 12mm; }
    .photo-page { page:photo-p; page-break-before:always; page-break-inside:avoid; width:100%; box-sizing:border-box; }
    .photo-page-lh { display:none; }
    .photo-sep { page:photo-p; page-break-before:always; display:flex; align-items:center; justify-content:center; min-height:180mm; }
    @page legal-doc-p { size:A4 landscape; margin:10mm 14mm; }
    .legal-doc-page { page:legal-doc-p; page-break-before:always; page-break-inside:avoid; width:100%; box-sizing:border-box; }
    .legal-doc-sep  { page:legal-doc-p; page-break-before:always; display:flex; align-items:center; justify-content:center; min-height:180mm; }
    .photo-page-header { display:flex; justify-content:space-between; align-items:center; font-family:"Times New Roman",Times,serif; font-size:10pt; font-weight:bold; border-top:1.5pt solid #1a3a5c; border-bottom:0.75pt solid #1a3a5c; padding:4pt 0; margin-bottom:8pt; color:#1a3a5c; text-transform:uppercase; letter-spacing:0.4px; }
    .photo-grid-4 { display:grid; grid-template-columns:1fr 1fr; grid-template-rows:1fr 1fr; gap:8pt; height:175mm; }
    .photo-cell { display:flex; flex-direction:column; border:0.75pt solid #ccc; border-radius:2pt; overflow:hidden; background:#fafafa; page-break-inside:avoid; }
    .photo-cell-empty { background:#f5f5f5 !important; border:0.75pt dashed #ddd !important; }
    .photo-img { flex:1; width:100%; object-fit:cover; display:block; min-height:0; }
    .photo-caption { font-family:"Times New Roman",Times,serif; font-size:8.5pt; font-style:italic; text-align:center; padding:2pt 4pt; background:#eef2f7; border-top:0.5pt solid #ddd; color:#333; flex-shrink:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    @media screen {
      .photo-page { background:white; max-width:277mm; margin:20px auto; padding:10mm 12mm; box-shadow:0 2px 12px rgba(0,0,0,0.12); }
      .legal-doc-page { background:white; max-width:277mm; margin:20px auto; padding:10mm 14mm; box-shadow:0 2px 12px rgba(0,0,0,0.12); }
      .photo-grid-4 { height:auto; }
      .photo-img { height:170px; }
    }
    @media print {
      html, body { background:white !important; margin:0 !important; padding:0 !important; }
      .photo-page { box-shadow:none !important; margin:0 !important; padding:0 !important; }
      .legal-doc-page { box-shadow:none !important; margin:0 !important; padding:0 !important; }
      .photo-grid-4 { height:175mm; }
      .no-print { display:none !important; height:0 !important; }
      body { print-color-adjust:exact; -webkit-print-color-adjust:exact; }
    }
    .report-header {
      text-align: center;
      border-top: 4px solid #000;
      border-bottom: 3px double #000;
      padding: 14px 0 12px;
      margin-bottom: 18px;
    }
    .report-header .org-name {
      font-size: 11pt; font-weight: bold; letter-spacing: 1.5px;
      text-transform: uppercase; margin-bottom: 4px; color: #333;
    }
    .report-header h1 {
      font-size: 18pt; font-weight: bold;
      text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 4px;
    }
    .report-header h2 {
      font-size: 13pt; font-weight: normal; margin-bottom: 8px;
    }
    .badge {
      display: inline-block;
      border: 2px solid #000;
      padding: 3px 20px;
      font-size: 11pt; font-weight: bold; letter-spacing: 3px;
      text-transform: uppercase;
    }
    .sec-h3 {
      font-size: 12pt; font-weight: bold;
      background: #fff5ee; color: #000;
      padding: 4px 10px;
      margin: 18px 0 8px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      page-break-after: avoid;
      border-left: 4pt solid #f4a261;
    }
    .sec-num {
      display: inline-block;
      background: #f4a261; color: #000;
      font-size: 10pt; font-weight: bold;
      padding: 0 6px;
      margin-right: 6px;
      border-radius: 2px;
    }
    .sub-head {
      font-size: 12pt; font-weight: bold;
      margin: 10px 0 5px;
      border-bottom: 1px solid #000;
      padding-bottom: 2px;
    }
    p { margin-bottom: 6pt; }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 10pt;
      font-size: 11pt;
      font-family: "Times New Roman", Times, serif;
    }
    th {
      background: #e8e8e8; color: #000;
      padding: 5pt 7pt;
      text-align: left;
      font-size: 10pt;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      border: 1pt solid #bbb;
    }
    td {
      padding: 4pt 7pt;
      border: 0.75pt solid #aaa;
      vertical-align: top;
    }
    tr:nth-child(even) td { background: #f7f7f7; }
    tr:hover td { background: #f0f0f0; }
    .info-table td { border: 0.75pt solid #bbb; padding: 4pt 7pt; font-size: 11.5pt; }
    .info-table td:nth-child(odd) {
      background: #efefef; font-weight: bold;
      width: 20%; font-size: 10pt; text-transform: uppercase; color: #333;
    }
    .total-row td {
      background: #d0d0d0 !important; color: #000 !important;
      font-weight: bold; border-top: 2pt solid #888;
      font-size: 12pt;
    }
    .summary-final {
      border: 2pt solid #000;
      padding: 14pt;
      margin: 14pt 0;
      page-break-inside: avoid;
    }
    .summary-final h4 {
      font-size: 13pt; font-weight: bold;
      margin-bottom: 10pt; text-align: center;
      text-transform: uppercase; letter-spacing: 1px;
      border-bottom: 1pt solid #000; padding-bottom: 6pt;
    }
    .summary-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: 8pt 12pt; margin-bottom: 6pt; border: 1pt solid #888;
    }
    .summary-row.comm { background: #e8f4f0; border-color: #2d6a4f; }
    .summary-row.fmv  { background: #fdf4ec; border-color: #8b4513; }
    .summary-row.dist { background: #fff0f0; border-color: #c0392b; }
    .summary-row .lbl { font-weight: bold; font-size: 12pt; }
    .summary-row .amt { font-weight: bold; font-size: 14pt; }
    .in-words-box {
      border: 1.5pt solid currentColor;
      padding: 7pt 10pt; margin: 5pt 0 12pt;
      font-size: 11.5pt; font-style: italic;
      page-break-inside: avoid;
    }
    .remarks-box {
      border: 1pt solid #888; padding: 10pt;
      margin-bottom: 10pt; min-height: 50pt;
      white-space: pre-wrap; font-size: 12pt;
    }
    .sig-block {
      display: grid; grid-template-columns: 1fr 1fr 1fr;
      gap: 30pt; margin-top: 50pt;
      page-break-inside: avoid;
    }
    .sig-line {
      border-top: 1pt solid #000; padding-top: 4pt;
      font-size: 11pt; text-align: center;
    }
    .sig-date { margin-top: 20pt; font-size: 11pt; }
    .disclaimer {
      font-size: 9.5pt; color: #555; font-style: italic;
      border-top: 0.5pt solid #ccc; padding-top: 8pt; margin-top: 14pt;
    }
    .page-break { page-break-before: always; }
    .no-break { page-break-inside: avoid; }
    .highlight-green { color: #2e7d32; font-weight: bold; }
    .highlight-orange { color: #e67e22; font-style: italic; }
  </style></head>
  <body>

  <div class="no-print">
    <span style="font-weight:600;flex:1">📄 ${esc(suggestedFilename, "Preliminary Valuation Report")}</span>
    <button class="btn-print" onclick="window.print()" title="Or press Ctrl+P / ⌘P">🖨️ Ctrl+P to Print / Save as PDF</button>
    <button class="btn-close" onclick="window.close()">✕ Close</button>
  </div>

  <div class="page">

  ${lhBlock}
  <!-- Report Cover Header -->
  <div class="report-header">
    <div class="org-name">${esc(s.bank, "Bank Name")}${s.branch?" — "+esc(s.branch):""}</div>
    <h1>Property Valuation Report</h1>
    <h2>Security Assessment for Loan / Mortgage</h2>
    <div class="badge">Preliminary Report</div>
    <div style="margin-top:8pt;font-size:11pt;color:#333">
      Date of Field Visit: <strong>${esc(s.visitDate)}</strong>
      &emsp;|&emsp;
      Date of Reporting: <strong>${esc(s.reportDate)}</strong>
    </div>
  </div>

  <!-- 1. Bank Name and Branch -->
  <h3 class="sec-h3"><span class="sec-num">1</span>Bank Name and Branch</h3>
  <table class="info-table">
    <tr><td>Bank</td><td>${esc(s.bank)}</td><td>Branch</td><td>${esc(s.branch)}</td></tr>
  </table>

  <!-- 2. Date of Valuation and Date of Field Visit -->
  <h3 class="sec-h3"><span class="sec-num">2</span>Date of Valuation and Date of Field Visit</h3>
  <table class="info-table">
    <tr><td>Date of Field Visit</td><td>${esc(s.visitDate)}</td><td>Date of Reporting / Valuation</td><td>${esc(s.reportDate)}</td></tr>
  </table>

  <!-- 3. General Information -->
  <h3 class="sec-h3"><span class="sec-num">3</span>General Information</h3>

  <p class="sub-head">3.1 Client's Information</p>
  ${clientsHTML||`<p style="font-style:italic;color:#888">No client information entered.</p>`}

  <p class="sub-head">3.2 Owner's Information</p>
  ${ownersInfoHTML||`<p style="font-style:italic;color:#888">No owner information entered.</p>`}

  <p class="sub-head">3.3 Detail of Property Information</p>
  <table>
    <thead>
      <tr><th>Plot No.</th><th>Trace Sheet</th><th>Land Type</th><th>Category</th><th>Area (Lalpurja)</th><th>sq.m</th><th>R-A-P-D</th><th>Owner Name</th><th>Address</th><th>Location</th><th>Plus Code</th></tr>
    </thead>
    <tbody>${propDetailRows||`<tr><td colspan="11" style="text-align:center;font-style:italic">No property data</td></tr>`}</tbody>
  </table>

  <!-- 3.4 Location Maps -->
  <p class="sub-head">3.4 Location Maps</p>
  ${(s.properties||[]).map(p => {
    const lat = parseFloat(p.lat);
    const lng = parseFloat(p.lng);
    const hasCoords = !isNaN(lat) && !isNaN(lng);
    const googleMapsUrl = hasCoords ? `https://www.google.com/maps?q=${lat.toFixed(6)},${lng.toFixed(6)}` : "";
    const snap = mapSnapshots[p.id] || {};
    const img15 = snap.z15 || "";
    const img18 = snap.z18 || "";
    return (
      '<div style="margin-bottom:14pt;page-break-inside:avoid">' +
      '<p style="font-weight:bold;font-size:11pt;margin-bottom:6pt">' +
        'Plot No. ' + (p.plotNo||'\u2014') + ' &nbsp;|&nbsp; ' + (p.location||'') + ' &nbsp;|&nbsp; Plus Code: ' + (p.googlePlusCode||'\u2014') +
      '</p>' +
      '<table class="info-table" style="margin-bottom:6pt"><tr>' +
        '<td>Latitude</td><td>' + (hasCoords ? lat.toFixed(6) : '\u2014') + '</td>' +
        '<td>Longitude</td><td>' + (hasCoords ? lng.toFixed(6) : '\u2014') + '</td>' +
        (hasCoords ? '<td><a href="' + googleMapsUrl + '" style="color:#2d6a4f;font-weight:bold">View in Google Maps</a></td>' : '<td>\u2014</td>') +
      '</tr></table>' +
      (hasCoords
        ? '<div style="display:flex;flex-direction:column;gap:12pt">' +
            '<div style="page-break-inside:avoid">' +
              '<p style="font-size:10pt;font-weight:bold;margin-bottom:4pt;padding:3pt 8pt;background:#f5f0eb;border-left:3pt solid #f4a261">Overview Map (Area Context)</p>' +
              (img15
                ? '<img src="' + img15 + '" style="width:100%;height:auto;border:1pt solid #bbb;border-radius:4pt;display:block" alt="Overview map" />'
                : '<div style="padding:20pt;border:1pt solid #ddd;text-align:center;font-style:italic;color:#888;font-size:10pt;background:#fafafa">Map not available \u2014 <a href="' + googleMapsUrl + '" style="color:#2d6a4f">View in Google Maps</a></div>') +
              '<p style="font-size:8.5pt;color:#888;text-align:right;margin-top:2pt;font-style:italic">\u00a9 OpenStreetMap contributors</p>' +
            '</div>' +
            '<div style="page-break-inside:avoid">' +
              '<p style="font-size:10pt;font-weight:bold;margin-bottom:4pt;padding:3pt 8pt;background:#f5f0eb;border-left:3pt solid #f4a261">Detail Map (Property Location)</p>' +
              (img18
                ? '<img src="' + img18 + '" style="width:100%;height:auto;border:1pt solid #bbb;border-radius:4pt;display:block" alt="Detail map" />'
                : '<div style="padding:20pt;border:1pt solid #ddd;text-align:center;font-style:italic;color:#888;font-size:10pt;background:#fafafa">Map not available \u2014 <a href="' + googleMapsUrl + '" style="color:#2d6a4f">View in Google Maps</a></div>') +
              '<p style="font-size:8.5pt;color:#888;text-align:right;margin-top:2pt;font-style:italic">\u00a9 OpenStreetMap contributors</p>' +
            '</div>' +
          '</div>'
        : '<p style="font-style:italic;color:#888;font-size:11pt;padding:10pt;border:1pt solid #ddd;text-align:center">No coordinates entered for this property.</p>'
      ) +
      '</div>'
    );
  }).join('<div style="border-top:1pt dashed #ccc;margin:10pt 0"></div>')||`<p style="font-style:italic;color:#888">No property data.</p>`}

  <!-- 4. Area to be Mortgaged -->
  <h3 class="sec-h3"><span class="sec-num">4</span>Area to Be Mortgaged</h3>
  <table>
    <thead><tr><th>Plot No.</th><th>Trace Sheet No.</th><th>Owner Name</th><th>Area (Lalpurja)</th><th>R-A-P-D</th><th>sq.m</th><th>Measured (sq.m)</th></tr></thead>
    <tbody>${mortAreaRows||`<tr><td colspan="7" style="text-align:center;font-style:italic">No mortgaged properties selected</td></tr>`}</tbody>
  </table>

  <!-- 5. Deduction Declaration -->
  <h3 class="sec-h3"><span class="sec-num">5</span>Deduction Declaration</h3>
  <table>
    <thead><tr><th>Plot No.</th><th>Dimension</th><th>Deduction Area (sq.m)</th><th>Reason / Remarks</th></tr></thead>
    <tbody>${deductionRows||`<tr><td colspan="4" style="text-align:center;font-style:italic">No deductions</td></tr>`}</tbody>
  </table>

  <!-- 6. Considered Area for Valuation -->
  <h3 class="sec-h3"><span class="sec-num">6</span>Considered Area for Valuation</h3>
  <table>
    <thead><tr><th>Plot No.</th><th>Lalpurja (sq.m)</th><th>Measured (sq.m)</th><th>Deduction (sq.m)</th><th>Considered (sq.m)</th><th>R-A-P-D</th><th>Aana</th></tr></thead>
    <tbody>${consideredRows||`<tr><td colspan="7" style="text-align:center;font-style:italic">No data</td></tr>`}</tbody>
  </table>

  <!-- 7. Access to Property -->
  <h3 class="sec-h3"><span class="sec-num">7</span>Access to Property</h3>
  <table>
    <thead><tr><th>Plot No.</th><th>Type of Road</th><th>Frontage of Plot</th><th>Width (Field)</th><th>Width (Trace)</th><th>Remarks</th></tr></thead>
    <tbody>${roadAccessHTML||`<tr><td colspan="6" style="text-align:center;font-style:italic">No road access data</td></tr>`}</tbody>
  </table>

  <!-- 8. Boundary Declaration -->
  <h3 class="sec-h3"><span class="sec-num">8</span>Boundary Declaration</h3>
  <table>
    <thead><tr><th>Plot No.</th><th>East</th><th>West</th><th>North</th><th>South</th><th>Remarks</th></tr></thead>
    <tbody>${boundaryRows||`<tr><td colspan="6" style="text-align:center;font-style:italic">No boundary data</td></tr>`}</tbody>
  </table>

  <!-- 9. Building Detail -->
  <h3 class="sec-h3"><span class="sec-num">9</span>Building Detail</h3>
  ${s.hasBuilding===false
    ? `<p style="font-style:italic;color:#888">No building available on this property.</p>`
    : s.hasBuilding==="skip"
    ? `<p style="font-style:italic;color:#e67e22">Building exists on this property but valuation of building has not been carried out.</p>`
    : (s.buildings||[]).length
      ? buildingDetailHTML
      : `<p style="font-style:italic;color:#888">No building data entered.</p>`}

  <!-- 10. Valuation of Land -->
  <h3 class="sec-h3"><span class="sec-num">10</span>Valuation of Land</h3>
  <table>
    <thead><tr><th>Plot No.</th><th>Considered (sq.m)</th><th>R-A-P-D</th><th>Aana</th><th>Commercial Rate (NPR/Aana)</th><th>FMV Rate (NPR/Aana)</th><th>Commercial Value (NPR)</th><th>FMV Value (NPR)</th></tr></thead>
    <tbody>${landValRows||`<tr><td colspan="8" style="text-align:center;font-style:italic">No data</td></tr>`}
      <tr class="total-row">
        <td colspan="6"><strong>TOTAL LAND VALUE (Rounded)</strong></td>
        <td><strong>NPR ${totalLandCommercial.toLocaleString("en-NP")}</strong></td>
        <td><strong>NPR ${totalLandFMV.toLocaleString("en-NP")}</strong></td>
      </tr>
    </tbody>
  </table>

  <!-- 11. Valuation of Building -->
  ${(s.buildings||[]).length && s.hasBuilding===true ? `
  <h3 class="sec-h3"><span class="sec-num">11</span>Valuation of Building</h3>
  ${buildingValHTML}
  <table style="margin-top:6px"><tbody>
    <tr class="total-row"><td><strong>TOTAL BUILDING VALUE (Rounded)</strong></td><td style="text-align:right"><strong>NPR ${totalBuildingValue.toLocaleString("en-NP")}</strong></td></tr>
  </tbody></table>` : s.hasBuilding==="skip"
    ? `<h3 class="sec-h3"><span class="sec-num">11</span>Valuation of Building</h3><p style="font-style:italic;color:#e67e22">Building exists on this property but valuation of building has not been carried out.</p>`
    : `<h3 class="sec-h3"><span class="sec-num">11</span>Valuation of Building</h3><p style="font-style:italic;color:#888">Not applicable.</p>`}

  <!-- 12. Valuation Summary -->
  <h3 class="sec-h3" style="page-break-before:always"><span class="sec-num">12</span>Valuation Summary</h3>
  <div class="summary-final" style="padding:8pt;margin:6pt 0">
    <h4 style="font-size:12pt;margin-bottom:6pt;padding-bottom:4pt">Summary of Property Valuation</h4>

    <p style="font-weight:bold;font-size:11pt;margin:6pt 0 3pt;color:#2d6a4f">A. Commercial Value of Property</p>
    <table style="margin-bottom:3pt;font-size:11pt">
      <tbody>
        <tr><td style="padding:3pt 7pt">1. Value of Land</td><td style="text-align:right;width:40%;padding:3pt 7pt">NPR ${totalLandCommercial.toLocaleString("en-NP")}</td></tr>
        ${(s.buildings||[]).length&&s.hasBuilding===true?`<tr><td style="padding:3pt 7pt">2. Value of Building</td><td style="text-align:right;padding:3pt 7pt">NPR ${totalBuildingValue.toLocaleString("en-NP")}</td></tr>`:""}
        <tr class="total-row"><td><strong>Total Commercial Value (A)</strong></td><td style="text-align:right"><strong>NPR ${commercialVal.toLocaleString("en-NP")}</strong></td></tr>
      </tbody>
    </table>
    <div class="in-words-box" style="color:#2d6a4f;border-color:#2d6a4f;padding:4pt 8pt;margin:3pt 0 8pt;font-size:11pt">
      In Words: Nepalese Rupees ${toWords(commercialVal)} Only &nbsp;(NPR ${Math.round(commercialVal).toLocaleString("en-NP")}/-)
    </div>

    <p style="font-weight:bold;font-size:11pt;margin:6pt 0 3pt;color:#8b4513">B. Fair Market Value (FMV)</p>
    <table style="margin-bottom:3pt;font-size:11pt">
      <tbody>
        <tr><td style="padding:3pt 7pt">1. Value of Land</td><td style="text-align:right;width:40%;padding:3pt 7pt">NPR ${totalLandFMV.toLocaleString("en-NP")}</td></tr>
        ${(s.buildings||[]).length&&s.hasBuilding===true?`<tr><td style="padding:3pt 7pt">2. Value of Building</td><td style="text-align:right;padding:3pt 7pt">NPR ${totalBuildingValue.toLocaleString("en-NP")}</td></tr>`:""}
        <tr class="total-row"><td><strong>Total Fair Market Value (B)</strong></td><td style="text-align:right"><strong>NPR ${fmv.toLocaleString("en-NP")}</strong></td></tr>
      </tbody>
    </table>
    <div class="in-words-box" style="color:#8b4513;border-color:#8b4513;padding:4pt 8pt;margin:3pt 0 8pt;font-size:11pt">
      In Words: Nepalese Rupees ${toWords(fmv)} Only &nbsp;(NPR ${Math.round(fmv).toLocaleString("en-NP")}/-)
    </div>

    <p style="font-weight:bold;font-size:11pt;margin:6pt 0 3pt;color:#c0392b">C. Distress Value (${Math.round(distressMult*100)}% of FMV)</p>
    <table style="margin-bottom:3pt;font-size:11pt">
      <tbody>
        <tr><td style="padding:3pt 7pt">1. Value of Land (${Math.round(distressMult*100)}% of FMV Land)</td><td style="text-align:right;width:40%;padding:3pt 7pt">NPR ${(Math.floor(totalLandFMV*distressMult/100)*100).toLocaleString("en-NP")}</td></tr>
        ${(s.buildings||[]).length&&s.hasBuilding===true?`<tr><td style="padding:3pt 7pt">2. Value of Building (${Math.round(distressMult*100)}% of Building Value)</td><td style="text-align:right;padding:3pt 7pt">NPR ${(Math.floor(totalBuildingValue*distressMult/100)*100).toLocaleString("en-NP")}</td></tr>`:""}
        <tr class="total-row"><td><strong>Total Distress Value (C)</strong></td><td style="text-align:right"><strong>NPR ${distressVal.toLocaleString("en-NP",{maximumFractionDigits:2})}</strong></td></tr>
      </tbody>
    </table>
    <div class="in-words-box" style="color:#c0392b;border-color:#c0392b;padding:4pt 8pt;margin:3pt 0 8pt;font-size:11pt">
      In Words: Nepalese Rupees ${toWords(distressVal)} Only &nbsp;(NPR ${Math.round(distressVal).toLocaleString("en-NP")}/-)
    </div>

    ${finalGovVal>0?`
    <p style="font-weight:bold;font-size:11pt;margin:6pt 0 3pt;color:#1565c0">D. Government Value of Land</p>
    <table style="margin-bottom:3pt;font-size:11pt">
      <tbody>
        <tr class="total-row"><td><strong>Total Government Value (D)</strong></td><td style="text-align:right"><strong>NPR ${finalGovVal.toLocaleString("en-NP")}</strong></td></tr>
      </tbody>
    </table>
    <div class="in-words-box" style="color:#1565c0;border-color:#1565c0;padding:4pt 8pt;margin:3pt 0 8pt;font-size:11pt">
      In Words: Nepalese Rupees ${toWords(finalGovVal)} Only &nbsp;(NPR ${Math.round(finalGovVal).toLocaleString("en-NP")}/-)
    </div>`:""}

    <div style="display:grid;grid-template-columns:1fr;gap:4pt;margin-top:8pt">
      <div class="summary-row comm" style="padding:5pt 10pt"><span class="lbl" style="font-size:11pt">A. Commercial Value of Property</span><span class="amt" style="font-size:12pt">NPR ${commercialVal.toLocaleString("en-NP")}</span></div>
      <div class="summary-row fmv"  style="padding:5pt 10pt"><span class="lbl" style="font-size:11pt">B. Fair Market Value (FMV)</span><span class="amt" style="font-size:12pt">NPR ${fmv.toLocaleString("en-NP")}</span></div>
      <div class="summary-row dist" style="padding:5pt 10pt"><span class="lbl" style="font-size:11pt">C. Distress Value (${Math.round(distressMult*100)}% of FMV)</span><span class="amt" style="font-size:12pt">NPR ${distressVal.toLocaleString("en-NP",{maximumFractionDigits:2})}</span></div>
      ${finalGovVal>0?`<div class="summary-row" style="padding:5pt 10pt;background:#e8f0fe;border-left:3pt solid #1565c0"><span class="lbl" style="font-size:11pt;color:#1565c0">D. Government Value</span><span class="amt" style="font-size:12pt;color:#1565c0">NPR ${finalGovVal.toLocaleString("en-NP")}</span></div>`:""}
    </div>
  </div>

  <!-- 13. Limiting Conditions and Remarks -->
  <h3 class="sec-h3" style="margin-top:10pt"><span class="sec-num">13</span>Limiting Conditions and Remarks</h3>
  <p style="font-weight:bold;font-size:11pt;margin:5pt 0 3pt">Remarks:</p>
  <div class="remarks-box" style="min-height:30pt;padding:6pt;font-size:11pt;margin-bottom:6pt">${esc(s.remarks)}</div>
  <p style="font-weight:bold;font-size:11pt;margin:5pt 0 3pt">Limiting Conditions:</p>
  <div class="remarks-box" style="min-height:30pt;padding:6pt;font-size:11pt;margin-bottom:10pt">${esc(s.limitingConditions, "This valuation is valid for six months from the date of reporting and is subject to verification of title documents, physical demarcation of boundary, and prevailing market conditions.")}</div>

  <div style="margin-top:30pt;display:flex;justify-content:flex-end">
    <div style="text-align:center;font-family:'Times New Roman',Times,serif;font-size:11.5pt;line-height:1.8">
      <div style="border-top:1pt solid #000;padding-top:8pt;min-width:200pt">
        <div style="font-weight:bold">For ${esc(vi.company, "Neo-Civic Consulting (P). Ltd")}</div>
        <div style="margin-top:4pt">${esc(vi.name, "Er. Saakar Rimal")}</div>
        ${vi.licenseNo?`<div>NEC Registration No. :- ${esc(vi.licenseNo)}</div>`:`<div>NEC Registration No. :- 11518 Civil &ldquo;A&rdquo;</div>`}
        ${vi.phone?`<div style="font-size:10pt">Tel. ${esc(vi.phone)}</div>`:""}
        ${vi.email?`<div style="font-size:10pt">Email: ${esc(vi.email)}</div>`:""}
      </div>
    </div>
  </div>

  <p class="disclaimer">
    Report Type: PRELIMINARY &nbsp;|&nbsp;
    Generated: ${new Date().toLocaleDateString("en-NP",{year:"numeric",month:"long",day:"numeric"})} &nbsp;|&nbsp;
    ${suggestedFilename||""} &nbsp;|&nbsp;
    This report is prepared for the sole use of ${esc(s.bank, "the Bank")} and may not be used for any other purpose.
  </p>

  </div>

  ${prelim_photoPages}

  <!-- Fixed page-number footer (printed on every page) -->
  <div class="page-footer">
    <span>Property Valuation Report — PRELIMINARY &nbsp;|&nbsp; ${esc(s.bank, "")} ${s.branch?'· '+esc(s.branch):""}</span>
    <span>${esc(suggestedFilename, "Preliminary Valuation Report")}</span>
  </div>

${purifyScript}${autoScript}</body></html>`;
}
